import AppContext from "PFApp/app_context";
import { CalendarMode, CalendarView, Row } from "PFApp/booking/types";
import { ProjectItemResource, RowType } from "PFApp/booking/types/row";
import { TEMPLATE_KEYS } from "PFApp/constants/templates";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { getOriginalResource } from "../../../helpers";
import { useBookingRowsContext } from "../../context/booking_rows_context";
import { useBookingsOverviewContext } from "../../context/bookings_overview_context";
import { RowMenuItem } from "./row_menu";

interface UseRowMenuShowInTabOption {
  row: Row;
}

export const useRowMenuShowInTabOption = ({ row }: UseRowMenuShowInTabOption): RowMenuItem => {
  const {
    store: {
      pages: {
        booking: { changeTab }
      }
    }
  } = useContext(AppContext);
  const {
    data: { templates }
  } = useCurrentProfile();
  const { calendarMode } = useBookingsOverviewContext();
  const { isEditModeEnabled } = useBookingRowsContext();
  const { t } = useTranslation("bookingModule");

  const showInTabLabels = useMemo(
    () => ({
      [CalendarMode.Workforce]: t("workforce.rowMenu.showInTab"),
      [CalendarMode.Projects]: t("projects.rowMenu.showInTab")
    }),
    [t]
  );

  const initialDataToApply = useMemo(() => {
    switch (row.type) {
      case RowType.Profile: {
        const profileId = getOriginalResource(row, calendarMode)?.id;
        const filters =
          calendarMode === CalendarMode.Projects
            ? { fields: { workforce_member_id: profileId } } // eslint-disable-line camelcase
            : { fields: { profile_id: profileId } }; // eslint-disable-line camelcase
        return { filters };
      }
      case RowType.Role:
      case RowType.Engagement: {
        const { id, template_id: templateId } = (getOriginalResource(row, calendarMode) ||
          {}) as ProjectItemResource;
        const templateKey = templates.find(({ id }) => id === templateId)?.key || "";
        const view = [TEMPLATE_KEYS.ENGAGEMENT, TEMPLATE_KEYS.AUDIT_ENGAGEMENT].includes(templateKey)
          ? CalendarView.EngagementsAndAuditEngagements
          : CalendarView.RolesAndAuditRoles;
        if (calendarMode === CalendarMode.Projects) {
          return {
            filters: { fields: { activity_id: id } } // eslint-disable-line camelcase
          };
        } else {
          return {
            filters: { fields: { id } },
            view
          };
        }
      }
      default:
        return {};
    }
  }, [row, calendarMode, templates]);

  const handleShowInTab = useCallback(
    (calendarMode: CalendarMode) => {
      changeTab("overview", {
        calendarMode,
        data: initialDataToApply
      });
    },
    [changeTab, initialDataToApply]
  );

  return {
    id: "showInTab",
    label: showInTabLabels[calendarMode],
    icon: "filter",
    disabled: isEditModeEnabled,
    item: () =>
      handleShowInTab(calendarMode === CalendarMode.Projects ? CalendarMode.Workforce : CalendarMode.Projects)
  };
};
